import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS, type InvitePreview } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { sessionKey } from '../lib/session';
import { AuthLayout } from '../components/AuthLayout';
import { useAvailability } from '../lib/availability';
import { NewPasswordField } from '../components/PasswordField';
import { Alert, Button, Field } from '../components/ui';

export function SignupPage() {
  const [params] = useSearchParams();
  const inviteToken = params.get('invite') ?? undefined;
  // Where to go after confirming and signing in (e.g. back to a club invite).
  const next = params.get('next')?.startsWith('/') && !params.get('next')!.startsWith('//') ? params.get('next')! : null;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [password, setPassword] = useState('');

  const invite = useQuery({
    queryKey: ['invite', inviteToken],
    queryFn: () => api.get<InvitePreview>(`/invites/${inviteToken}`),
    enabled: !!inviteToken,
  });
  useEffect(() => {
    if (invite.data) setEmail(invite.data.email);
  }, [invite.data]);

  const signup = useMutation({
    mutationFn: () => api.post<{ status: 'check_email' } | { status: 'signed_in'; teamId: string }>('/auth/signup', { name, email, password, inviteToken }),
    onSuccess: async (r) => {
      if (r.status === 'signed_in') {
        await qc.invalidateQueries({ queryKey: sessionKey });
        navigate(`/teams/${r.teamId}`, { replace: true });
      }
    },
  });

  const availability = useAvailability();
  if (availability.data && !availability.data.signups) {
    return (
      <AuthLayout title="Opening soon" description="Hoopify isn’t taking sign-ups just yet. Check back soon.">
        <p className="text-muted">
          Already have an account?{' '}
          <Link to="/login" className="text-action hover:underline">
            Sign in
          </Link>
        </p>
      </AuthLayout>
    );
  }

  if (signup.data?.status === 'check_email') {
    return (
      <AuthLayout
        title="Check your email"
        description={
          <>
            We sent a link to <strong className="font-semibold text-ink">{email}</strong>. Open it to confirm your address, then sign in.
          </>
        }
        footer={
          <>
            Nothing after a few minutes? Check your junk folder, or{' '}
            <button className="font-medium text-action hover:underline" onClick={() => signup.reset()}>
              try a different address
            </button>
            .
          </>
        }
      >
        <Link to={`/login?email=${encodeURIComponent(email)}${next ? `&next=${encodeURIComponent(next)}` : ''}`} className="btn-secondary w-full">
          Go to sign in
        </Link>
      </AuthLayout>
    );
  }

  const fields = fieldErrors(signup.error);
  const fromInvite = !!invite.data && !invite.data.expired;
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    signup.mutate();
  };

  return (
    <AuthLayout
      title={fromInvite ? `Join ${invite.data!.teamName}` : 'Create your account'}
      description={
        fromInvite
          ? `${invite.data!.invitedBy} invited you as ${ROLE_LABELS[invite.data!.role].toLowerCase()}. Set up your account to join.`
          : 'For head coaches setting up their team. Assistants, players and parents join through an invite from their coach.'
      }
      footer={
        <>
          Already have an account?{' '}
          <Link to={inviteToken ? `/login?next=${encodeURIComponent(`/invite/${inviteToken}`)}` : next ? `/login?next=${encodeURIComponent(next)}` : '/login'} className="font-medium text-action hover:underline">
            Sign in
          </Link>
          {!inviteToken && (
            <span className="mt-2 block">
              Setting up a club?{' '}
              <Link to="/clubs/signup" className="font-medium text-action hover:underline">
                Register your club
              </Link>
            </span>
          )}
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {invite.isError && <Alert tone="warn">{errorMessage(invite.error)} You can still create an account.</Alert>}
        {invite.data?.expired && <Alert tone="warn">This invite has expired. Ask your coach to send a new one. You can still create an account.</Alert>}
        {signup.isError && !Object.keys(fields).length && <Alert>{errorMessage(signup.error)}</Alert>}
        <Field label="Your name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} error={fields.name} required autoFocus />
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fields.email}
          readOnly={fromInvite}
          hint={fromInvite ? 'This is the address your invite was sent to.' : undefined}
          required
        />
        <NewPasswordField value={password} onChange={setPassword} error={fields.password} userInputs={[name, email]} />
        <Button type="submit" loading={signup.isPending} className="btn-primary btn-lg w-full">
          {fromInvite ? 'Create account and join' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
